import { ref, type Ref } from 'vue';
import * as Cesium from 'cesium';

interface GroundClippingReturn {
    isGroundClipped: Ref<boolean>;
    toggleClipping: () => Promise<void>;
    cleanupGroundClipping: () => void;
}

export function useGroundClipping(
    viewer: Ref<Cesium.Viewer | null>,
    targetTileset: Ref<Cesium.Cesium3DTileset | null>
): GroundClippingReturn {
    const isGroundClipped = ref<boolean>(false);
    const footprint = ref<Cesium.Entity | null>(null);
    const clippingPolygons = ref<Cesium.ClippingPolygonCollection | null>(null);
    const isInitialized = ref<boolean>(false);

    const footprintDataSource = ref<Cesium.GeoJsonDataSource | null>(null);

    async function loadFootprint(): Promise<void> {
        if (!viewer.value) return;

        try {
            const resource = await Cesium.IonResource.fromAssetId(4169205);
            const dataSource = await Cesium.GeoJsonDataSource.load(resource, {
                clampToGround: true,
            });

            footprintDataSource.value = dataSource;
            viewer.value.dataSources.add(dataSource);

            footprint.value =
                dataSource.entities.values.find((entity: Cesium.Entity) =>
                    Cesium.defined(entity.polygon)
                ) || null;

            if (footprint.value) {
                footprint.value.show = false;
            }
        } catch (error) {
            console.error(`Error loading geojson: ${error}`);
            throw error;
        }
    }

    function setupClippingPolygons(): void {
        if (!footprint.value || !footprint.value.polygon || !targetTileset.value) return;

        const positions =
            footprint.value.polygon.hierarchy!.getValue(
                Cesium.JulianDate.now()
            ).positions;

        clippingPolygons.value = new Cesium.ClippingPolygonCollection({
            polygons: [
                new Cesium.ClippingPolygon({
                    positions,
                }),
            ],
        });

        targetTileset.value.clippingPolygons = clippingPolygons.value;

        clippingPolygons.value.enabled = true;
        clippingPolygons.value.inverse = false;

        isGroundClipped.value = true;
    }

    async function toggleClipping(): Promise<void> {
        if (!viewer.value || !targetTileset.value) {
            console.error('Viewer or tileset not initialized');
            return;
        }

        if (!isInitialized.value) {
            try {
                await loadFootprint();
                if (footprint.value) {
                    setupClippingPolygons();
                    isInitialized.value = true;
                }
            } catch (error) {
                console.error('Error initializing clipping:', error);
            }
            return;
        }

        if (!clippingPolygons.value) return;

        if (!isGroundClipped.value) {
            clippingPolygons.value.enabled = true;
            clippingPolygons.value.inverse = false;
            isGroundClipped.value = true;
            viewer.value.scene.requestRender();
        } else {
            clippingPolygons.value.enabled = false;
            clippingPolygons.value.inverse = false;
            isGroundClipped.value = false;
            viewer.value.scene.requestRender();
        }
    }

    function cleanupGroundClipping(): void {
        if (clippingPolygons.value) {
            clippingPolygons.value.destroy();
            clippingPolygons.value = null;
        }

        if (viewer.value && footprintDataSource.value) {
            viewer.value.dataSources.remove(footprintDataSource.value);
            footprintDataSource.value = null;
        }

        footprint.value = null;
        isInitialized.value = false;
        isGroundClipped.value = false;
    }

    return {
        isGroundClipped,
        toggleClipping,
        cleanupGroundClipping,
    };
}
